/**
 * Real-User-Monitoring: Web Vitals nach Hydration erfassen und in Batches
 * per sendBeacon an /api/public/rum senden. Keine PII, keine Cookies.
 */

import { classifyRequest } from "@/lib/bot-detection";

type Metric = {

  name: "LCP" | "INP" | "CLS" | "FCP" | "TTFB";
  value: number;
  rating?: "good" | "needs-improvement" | "poor";
  navigationType?: string;
};

type QueuedMetric = {
  metric: Metric["name"];
  value: number;
  rating: Metric["rating"] | null;
  path: string;
  nav_type: string | null;
  device: "mobile" | "desktop";
  session_id: string;
};

const ENDPOINT = "/api/public/rum";
const SESSION_KEY = "creategy.rum.sid";
// 100 % in Preview/lokal, 25 % in Production
const PROD_SAMPLE_RATE = 0.25;
const IGNORED_PATH_PREFIXES = ["/admin", "/auth", "/api/"];

let initialized = false;
const queue: QueuedMetric[] = [];

function isProd(): boolean {
  const h = window.location.hostname;
  return !h.includes("localhost") && !h.includes("lovableproject.com") && !h.includes("id-preview");
}

function sampled(): boolean {
  if (!isProd()) return true;
  return Math.random() < PROD_SAMPLE_RATE;
}

function getSessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (crypto.randomUUID?.() ?? String(Date.now()) + Math.random().toString(36).slice(2)).slice(0, 64);
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return "no-session";
  }
}

function getDevice(): "mobile" | "desktop" {
  const uaData = (navigator as unknown as { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uaData && typeof uaData.mobile === "boolean") return uaData.mobile ? "mobile" : "desktop";
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? "mobile" : "desktop";
}

function shouldIgnorePath(path: string): boolean {
  return IGNORED_PATH_PREFIXES.some((p) => path.startsWith(p));
}

function flush(): void {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  const body = JSON.stringify({ metrics: batch });
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    }
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* swallow – RUM darf niemals die App stören */
  }
}

function enqueue(metric: Metric): void {
  const path = window.location.pathname.slice(0, 200);
  if (shouldIgnorePath(path)) return;
  queue.push({
    metric: metric.name,
    value: Math.round(metric.value * 1000) / 1000,
    rating: metric.rating ?? null,
    path,
    nav_type: metric.navigationType ?? null,
    device: getDevice(),
    session_id: getSessionId(),
  });
  if (queue.length >= 5) flush();
}

/** Automatisierte Zugriffe verfälschen die Messwerte – daher ausschließen. */
function isLikelyBot(): boolean {
  try {
    if ((navigator as unknown as { webdriver?: boolean }).webdriver) return true;
    const { classification } = classifyRequest({
      userAgent: navigator.userAgent,
      accept: "text/html",
      acceptLanguage: navigator.language || "de",
    });
    return classification !== "human";
  } catch {
    return false;
  }
}

export function initWebVitalsReporter(): void {
  if (initialized) return;
  if (typeof window === "undefined") return;
  if (isLikelyBot()) return;
  if (!sampled()) return;
  initialized = true;


  const start = async () => {
    try {
      const { onLCP, onINP, onCLS, onFCP, onTTFB } = await import("web-vitals");
      onLCP(enqueue);
      onINP(enqueue);
      onCLS(enqueue);
      onFCP(enqueue);
      onTTFB(enqueue);

      addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") flush();
      });
      addEventListener("pagehide", flush);
    } catch {
      /* web-vitals konnte nicht geladen werden – ignorieren */
    }
  };

  const idle = (window as unknown as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
  if (idle) idle(() => void start());
  else setTimeout(() => void start(), 1500);
}
